import type { FillRateFineReview } from "@/api/inbound_po_line.tsx"
import type { PurchaseOrder } from "@/api/purchase_order.tsx"
import { DeductionTask, TaskInfo } from "@/api/task.tsx"
import { Ok, Option, Result, Id, Uuid, Vec } from "@/utils"
import { api_fetch, HttpError, HttpResponse } from "./client"
import { DisputeJson } from "./dispute"
import { DeductionAction } from "./deduction_action"
import type { EmailAttachmentInput } from "./email_attachments"
import type { ContractRateCheck } from "./contract_term"

export type DeductionKind = "invoice" | "deduction" | "prepayment" | "repayment"

export interface SyncTableData {
  table: string
  integration: string
  integration_id: Uuid
}

export interface JobSchedule {
  id: string
  crontab: string
  data: SyncTableData
  integration_id: Uuid
}

export interface UserFile {
  display_name: string
  date: string
  s3_uri: string
  actor_name: string
}

export interface Deduction {
  id: string
  org_id: string
  created_at: string
  updated_at: string
  source: string
  /** ISO 4217 code every amount on the row is in (`USD` unless the payer pays in another). */
  currency?: string
  /** Dollars per unit of `currency`, for adding the row into totals; null on dollar rows. */
  reporting_fx_rate?: number | null
  invoice_amount: number
  discount_amount: number
  dc?: string
  dc_name?: string
  kind: DeductionKind
  gross_amount: number
  invoice_date: string
  invoice_number: string
  check_amount: number
  check_date: string
  check_number: string
  category_id: string
  backup_s3_uri: string | null
  check_s3_uri: string | null
  po_number?: string
  data: string
  document: string
  retailer_name: string | null
  /** Invoice-level admin fee (USD); shown/edited when org enables `admin_fee` for deduction detail. */
  admin_fee?: number | null
  execution_date: string
  /** When the deduction was posted to the accounting system; shown/edited when org enables `posting_date` for deduction detail. */
  posting_date?: string | null
  /** Date the deduction first reached the validated status. DB-maintained (trigger) but editable; surfaced as a top-level field on list/detail payloads. */
  validated_date?: string | null
  user_files: UserFile[]
  task?: TaskInfo
  dispute_id: string
  product_line: string
  retailer_invoice_number: string
  payment_due_date?: string
  original_source?: string
  custom_deduction_method?: string
  repayment_for?: string
  /** For "sales invoice discount" deductions: the linked kind=invoice sales invoice transaction. */
  sales_invoice_id?: string | null
  accounting_status?: string
  has_splits: boolean
  is_parent: boolean
  import_source: string
  description?: string
  pending: boolean
  remote_accounting_id?: string
  promotion_id?: string
}

export interface DeductionExt extends Deduction {
  category: string
  category_description: string
  category_dispute_email_to?: string | null
  status_value: string
  customer_name: string
  /** Retailer dispute playbook — populated on the detail JSON only (see `DisputePlaybook`). */
  dispute_playbook?: DisputePlaybook | null
}

/**
 * Where and how the retailer takes disputes and backup requests for this deduction, keyed off the
 * invoice prefix on the server (`db::deduction::dispute_playbook`). `channel: "email"` means
 * MarginWiz mails the dispute to `dispute_email`; `"portal"` means it is recorded here and filed
 * by the person in `portal_name`. `dispute_email` on a portal playbook is a contact, not a route.
 */
export interface DisputePlaybook {
  retailer: string
  deduction_type: string | null
  channel: "email" | "portal"
  dispute_email: string | null
  backup_email: string | null
  portal_name: string | null
  portal_url: string | null
  steps: string[]
  backup_steps: string[]
  notes: string[]
}

export interface DeductionNote {
  id: string
  created_at: string
  user_id: string
  deduction_id: string
  org_id: string
  message: string
  actor_name: string
  read_at: string | null
  invoice_number: string | null
  amount: number | null
}

export interface DeductionListJson extends DeductionExt {
  dispute_amount?: number
  last_activity_at?: string
  deduction_amount: number
  validated: boolean
  action?: DeductionAction
  has_notes: boolean
  has_triage_result?: boolean
  /** True when BigWiz has run on this deduction. */
  has_bigwiz_result?: boolean
  won_amount?: number
  /** Set when list API merged a repayment into this row (repayment row omitted from response). */
  has_matched_repayment?: boolean
  has_backup: boolean
  /** True when at least one parsed `backup` line exists for this deduction. */
  backup_parsed?: boolean
  /**
   * True when the deduction is linked to a promotion — either directly
   * (`deduction_promotion_match`) or via a GL-coding split carrying a promotion.
   */
  promotion_matched?: boolean
  /** Days from invoice to check; set for early-pay invoices when integration discount term applies. */
  early_pay_days?: number | null
  /** Classification from org integration early pay terms (Kehe/UNFI discount_term). */
  early_pay_window?: "within" | "debatable" | "outside" | "needs_receipt_date" | null
  /** When the primary dispute was filed (drives the "days since disputed" age basis). */
  dispute_created_at?: string | null
  /** When the deduction entered its current stage (latest status row's created_at). */
  status_changed_at?: string | null
  /** Last day a dispute can be filed (server-computed from the shared per-source window). */
  dispute_deadline?: string | null
}

export type QueueItemType =
  | "external_message"
  | "internal_message"
  | "completed_task"
  | "expiring_deduction"
  | "disputable_deduction"

export interface QueueItem {
  priority: number
  item_type: QueueItemType
  deduction_id: string
  deduction_amount: number
  deduction_invoice_number: string
  title: string
  description: string
  action_required: string
  last_touched_at: string | null
  created_at: string
  message_id: string | null
  note_id: string | null
  task_id: string | null
  days_until_expiration: number | null
  is_disputable: boolean
  message_read_at: string | null
  note_read_at: string | null
  deduction: DeductionListJson
  action: DeductionAction | null
}

export interface RecommendationRule {
  source?: string
  dc_region?: string
  dc_codes?: string[]
  product_type?: string
}

export interface LogisticsProvider {
  id?: string
  name: string
  email: string
  contact_name?: string
  details?: string
  cc_emails?: string[]
  access_method?: "portal" | "email"
  portal_url?: string
  portal_username?: string
  portal_password?: string
  recommendation_rules?: RecommendationRule[]
  is_recommended?: boolean
  recommendation_reason?: string
}

export interface RepaymentSummary {
  id: string
  invoice_number: string
  /** Absolute repayment amount. */
  amount: number
  invoice_date: string
  check_date?: string | null
  check_number?: string | null
}

/** Link between a sales invoice transaction and one of its on-invoice discount deductions. */
export interface SalesInvoiceLink {
  id: string
  invoice_number: string
  /** Signed amount as stored (invoice positive, discount deduction negative). */
  amount: number
  invoice_date: string
  /** The discount reason for discount deductions. */
  description?: string | null
}

export interface DeductionDetailJson extends DeductionExt {
  dispute: Option<DisputeJson>
  dispute_amount?: number
  /** All tasks on this deduction, newest first. The single `task` field only mirrors the preferred (latest pending) task. */
  tasks: DeductionTask[]
  messages: Message[]
  action?: DeductionAction
  notes: DeductionNote[]
  has_stord_config: boolean
  logistics_providers: LogisticsProvider[]
  logistics_provider_guidance_url?: string | null
  promotion_id?: string
  promotion_name?: string
  promotion_remote_id?: string
  repayment_id?: string
  repayment_amount?: number
  /** All repayments attached to this deduction (newest first). */
  repayments?: RepaymentSummary[]
  /** The sales invoice this on-invoice discount deduction was taken on. */
  sales_invoice?: SalesInvoiceLink
  /** On-invoice discount/allowance deductions linked to this sales invoice. */
  sales_invoice_discounts?: SalesInvoiceLink[]
  tracking_number?: string
  /** When the deduction entered its current stage (latest status row's created_at). */
  status_changed_at?: string | null
  /** Last day a dispute can be filed (server-computed from the shared per-source window). */
  dispute_deadline?: string | null
  has_backup: boolean
  /**
   * Why backup will never arrive, as user-facing prose. Present only when the source documents that
   * it publishes none (e.g. Tony's fee types whose original request *is* the backup). When set, the
   * "Request Backup" action is suppressed and this is shown instead.
   */
  backup_unavailable_reason?: string | null
  /** The retailer portal that holds the backup the reason above describes (H-E-B's AP-AR Application). */
  backup_portal?: { name: string; url: string } | null
  /** Portal/email backups besides current `backup_s3_uri` (detail API only). */
  distributor_backup_s3_uris?: string[]
  /** Deduction-linked PDFs too large for auto-OCR; the UI offers an explicit "Parse big file". */
  large_backup_uris?: string[]
  invoice_stem: string
  /** Email threads linked from outside the deduction's own conversation (full messages included). */
  related_emails: RelatedEmailThread[]
  /** Charged program rates vs the org's signed contract terms; absent when no term applies. */
  contract_rate_check?: ContractRateCheck | null
  /** UNFI Canada `LOBLAWS FILL RATE` chargebacks: the fine reviewed against receiving and Loblaw's policy. */
  fill_rate_fine_review?: FillRateFineReview
  /** The customer purchase order(s) this deduction names, with their lines, when on file. */
  purchase_orders?: PurchaseOrder[]
}

/** An email thread linked to a deduction from outside its own conversation (full messages included). */
export interface RelatedEmailThread {
  thread_id: string
  subject: string
  messages: Message[]
  /** s3 uris of this thread's attachments linked to the deduction (for the "Linked" badge). */
  linked_s3_uris: string[]
}

export interface LedgerAccount {
  id: string
  org_id: string
  name?: string
  code_type: string
  description?: string
  expense_account: string
  income_account?: string
  account_number?: string
  /** Accounting item (product/service) used on credit memo lines (QuickBooks/NetSuite). */
  product_service?: string | null
  /** When true, omit from coding pickers and AI wizards (still shown on existing splits). */
  hidden_from_deduction_matching?: boolean
}

export interface Recipient {
  name?: string
  email?: string
}

export interface Attachment {
  id: string
  created_at: string
  name: string
  s3_uri: string
  content_type: string
}

export interface Message {
  id: string
  org_id: string
  integration_id: string
  thread_id: string
  source: string

  dispute_id?: string
  deduction_id?: string

  remote_id: string
  sequence: number
  date: string
  from: Recipient
  to: Recipient[]
  cc: Recipient[]
  bcc: Recipient[]
  subject: string
  // deprecated
  body: string
  html_body: string
  inbound: boolean
  read_at: string | null
  attachments?: Vec<Attachment>
  thread_kind?: string
}

export interface UpdateDeductionPayload {
  kind?: DeductionKind
  po_number?: string
  retailer_name?: string
  execution_date?: string
  dc?: string
  repayment_for?: string
  pending?: boolean
  check_number?: string
  check_date?: string
  posting_date?: string
  validated_date?: string
  custom_deduction_method?: string
  revert_old_status?: boolean
  /** JSON number to set, or `null` to clear. Omit to leave unchanged. */
  admin_fee?: number | null
}

export interface DeductionSearchResult {
  id: string
  invoice_number: string
  invoice_date: string
  invoice_amount: number
  deduction_amount: number
  discount_amount?: number
  gross_amount?: number
  kind: DeductionKind
  retailer_name?: string
  po_number?: string
  category: string
  category_description: string
  customer_name?: string
  description?: string
  source: string
  dispute_id?: string
  dispute_amount?: number
}

export interface SearchDeductionsParams {
  q?: string
  source?: string
  before_date?: string
  exclude_repayments?: boolean
  kinds?: string // comma-separated: "deduction,invoice"
}

export async function searchDeductions(params: SearchDeductionsParams): Promise<DeductionSearchResult[]> {
  const queryParams: Record<string, string> = {}
  if (params.q) queryParams.q = params.q
  if (params.source) queryParams.source = params.source
  if (params.before_date) queryParams.before_date = params.before_date
  if (params.exclude_repayments) queryParams.exclude_repayments = "true"
  if (params.kinds) queryParams.kinds = params.kinds

  const response = await api_fetch<DeductionSearchResult[]>("/deduction/search", { params: queryParams })
  if (response.ok) {
    return response.value.data
  } else {
    throw new Error(`Failed to search deductions: ${response.error.message}`)
  }
}

export async function lookupDeductionById(id: string): Promise<Deduction | null> {
  const response = await api_fetch<Deduction | null>(`/deduction/lookup/${id}`)
  if (response.ok) {
    return response.value.data
  } else {
    throw new Error(`Failed to lookup deduction: ${response.error.message}`)
  }
}

export async function updateDeduction(id: string, payload: UpdateDeductionPayload): Promise<DeductionDetailJson> {
  const response = await fetch(`/api/deduction/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    throw new Error("Failed to update deduction")
  }

  return response.json()
}

interface UpdateActionOpenLink {
  type: "open_link"
  url: string
  title: string
}

interface UpdateActionMarkComplete {
  type: "mark_complete"
  title: "Mark complete"
  task_id: string
}

export type UpdateAction = UpdateActionOpenLink | UpdateActionMarkComplete

export interface DeductionUpdate {
  date: string
  title: string
  description?: String
  action?: UpdateAction
}

export async function get_updates(deduction_id: Id) {
  let res = await api_fetch<Vec<DeductionUpdate>>(`/deduction/${deduction_id}/updates`)
  if (res.ok) {
    return Ok(res.value.data)
  } else {
    return res
  }
}

export interface SendMessage {
  text: string
  from?: string
  to?: string[]
  cc?: string[]
  bcc?: string[]
  subject?: string | null
  attachments?: EmailAttachmentInput[]
  existing_attachment_s3_uris?: string[]
}

export async function send_message(
  deduction: Deduction,
  body: SendMessage
): Promise<Result<HttpResponse<void>, HttpError>> {
  let url = `/deduction/${deduction.id}/message`
  let res = await api_fetch<void>(url, { body })
  return res
}

export async function reply_to_thread(
  deduction_id: string,
  thread_id: string,
  text: string,
  attachments: EmailAttachmentInput[] = [],
  existing_attachment_s3_uris: string[] = []
): Promise<Result<HttpResponse<void>, HttpError>> {
  let url = `/deduction/${deduction_id}/thread/${thread_id}/reply`
  let res = await api_fetch<void>(url, { body: { text, attachments, existing_attachment_s3_uris } })
  return res
}

export type GenerateMessageType = "bump_message" | "backup_followup"

export interface GenerateMessageResponse {
  message: string
}

export async function generate_message(
  deduction_id: string,
  message_type: GenerateMessageType
): Promise<Result<HttpResponse<GenerateMessageResponse>, HttpError>> {
  let url = `/deduction/${deduction_id}/generate-message`
  let res = await api_fetch<GenerateMessageResponse>(url, { body: { message_type } })
  return res
}

export async function save_note(text: string, deduction_id: string) {
  let body = {
    note: text,
    deduction_id,
  }
  await api_fetch(`/deductions/note`, { body })
}

export async function move_message_attachments_to_user_files(
  deduction_id: string,
  message_id: string
): Promise<Result<HttpResponse<UserFile[]>, HttpError>> {
  let url = `/deduction/${deduction_id}/move-message-attachments`
  let res = await api_fetch<UserFile[]>(url, {
    body: { message_id },
  })
  return res
}

// Promotion Deductions API
export interface PromotionDeductionResponse {
  deduction: Deduction
  category: string
  category_description?: string
  status_value: string
  match_score: number
  match_reason: string
  is_manual: boolean
  customer_match: boolean
  retailer_match: boolean
  product_match: boolean
  date_match: boolean
  rate_match: boolean
  split_amount?: number
}

export async function getPromotionDeductions(promotionId: string): Promise<PromotionDeductionResponse[]> {
  const response = await api_fetch<PromotionDeductionResponse[]>(`/promotion-deductions`, {
    method: "POST",
    body: { promotion_id: promotionId },
  })

  if (response.ok) {
    return response.value.data
  } else {
    throw new Error(`Failed to fetch promotion deductions: ${response.error.message}`)
  }
}

export interface CreatePromotionMatchRequest {
  promotion_id: string
  deduction_id: string
}

export interface CreatePromotionMatchResponse {
  success: boolean
  match_id: string
  message: string
}

export async function createPromotionMatch(
  request: CreatePromotionMatchRequest
): Promise<CreatePromotionMatchResponse> {
  const response = await api_fetch<CreatePromotionMatchResponse>(`/promotion-match`, {
    method: "POST",
    body: request,
  })

  if (response.ok) {
    return response.value.data
  } else {
    throw new Error(`Failed to create promotion match: ${response.error.message}`)
  }
}

// Parse File API
export interface ParseFileResponse {
  file_kind: string
  success: boolean
  message?: string
}

export type ParseFileMode = "auto" | "gemini" | "azure_job"

export async function parseFile(
  deduction_id: string,
  s3_uri: string,
  mode: ParseFileMode = "auto"
): Promise<Result<HttpResponse<ParseFileResponse>, HttpError>> {
  const url = `/deduction/${deduction_id}/parse-file`
  const res = await api_fetch<ParseFileResponse>(url, {
    body: { s3_uri, mode },
  })
  return res
}

export interface BulkParseBackupResultItem {
  deduction_id: string
  skipped: boolean
  success: boolean
  message?: string
}

export interface BulkParseBackupResponse {
  results: BulkParseBackupResultItem[]
}

export async function bulk_parse_backup(
  deduction_ids: string[]
): Promise<Result<HttpResponse<BulkParseBackupResponse>, HttpError>> {
  return await api_fetch<BulkParseBackupResponse>("/deductions/parse-backup/bulk", {
    method: "POST",
    body: { deduction_ids },
  })
}

/** Remove `id` as a duplicate of `keep_id`: its notes, tasks, disputes, coding and files move to `keep_id`. */
export async function removeDuplicateDeduction(
  id: string,
  keep_id: string
): Promise<Result<HttpResponse<{ kept_id: string }>, HttpError>> {
  return api_fetch<{ kept_id: string }>(`/deduction/${id}?duplicate_of=${encodeURIComponent(keep_id)}`, { method: "DELETE" })
}
